<template>
    <Teleport
        defer
        :to="boundary"
    >
        <Transition name="popover">
            <div
                v-if="show"
                ref="root"
                class="popover"
                data-cy="popover"
                :class="computedClass"
                :style="styles"
                v-bind="$attrs"
            >
                <slot />
            </div>
        </Transition>
    </Teleport>
</template>

<script lang="ts" setup>
import { computed, toRef, useTemplateRef, watch } from 'vue';
import { useClickOutsideListener } from '../../composables/clickOutsideListener';
import { Variant } from './Popover/Variant';

const root = useTemplateRef<HTMLDivElement>('root');

type Styles = {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    width?: string;
    maxWidth?: string;
};

type TopLeftPosition = { top: number; left: number; };
type TopRightPosition = { top: number; right: number; };
type BottomLeftPosition = { bottom: number; left: number; };
type BottomRightPosition = { bottom: number; right: number; };

export type Position = TopLeftPosition
    | TopRightPosition
    | BottomLeftPosition
    | BottomRightPosition;

type Props = {
    position: Position;
    width?: number;
    unclampWidth?: boolean;
    variant?: Variant[keyof Variant] | null;
    customClass?: string;
    boundary?: string | HTMLElement;
    focus?: boolean;
    focusIds?: string[];
};

const props = withDefaults(defineProps<Props>(), {
    customClass: undefined,
    variant: Variant.PRIMARY,
    width: undefined,
    unclampWidth: false,
    boundary: 'body',
    focus: false,
    focusIds: () => [],
});

const show = defineModel<boolean>('show', { default: false });

type Emits = {
    (event: 'shown'): void;
    (event: 'hidden'): void;
};

const emit = defineEmits<Emits>();

const hidePopover = () => {
    if (show.value) {
        show.value = false;
    }
};

useClickOutsideListener(
    toRef(() => props.focus),
    [() => root.value, ...props.focusIds],
    (event) => {
        const toggleButton = document.querySelector('.chevron-wrapper');
        if (toggleButton?.contains(event.target as Node)) {
            return;
        }
        hidePopover();
    },
);

const styles = computed<Styles>(() => ({
    top: 'top' in props.position ? `${props.position.top}px` : undefined,
    bottom: 'bottom' in props.position ? `${props.position.bottom}px` : undefined,
    left: 'left' in props.position ? `${props.position.left}px` : undefined,
    right: 'right' in props.position ? `${props.position.right}px` : undefined,
    width: props.width ? `${props.width}px` : undefined,
    maxWidth: props.unclampWidth ? 'none' : undefined,
}));

const computedClass = computed(() => [
    `popover--${props.variant}`,
    props.customClass,
]);

watch(show, async (value) => {
    if (!value) {
        emit('hidden');
        return;
    }

    emit('shown');
});

defineExpose({ root });
</script>

<style lang="scss" scoped>
@import '../../../styles/abstracts/spacings';
@import '../../../styles/abstracts/variables';
@import '../../../styles/abstracts/font-sizes';
@import '../../../styles/abstracts/z-indexes';

.popover {
    position: absolute;
    z-index: $popover-z-index;

    max-width: 28.75rem;
    padding: 0;
    overflow: hidden;

    font-size: $font-size-base;

    border: 1px solid var(--theme-color-border-divider-primary);
    border-radius: $border-radius;
    box-shadow: var(--theme-shadow-primary);

    transition: opacity 0.2s ease;
}

.popover--primary {
    background-color: var(--theme-color-surface-primary-default);
}

.popover--secondary {
    color: var(--theme-color-text-inversed);

    background-color: var(--theme-color-surface-inversed-secondary);
}

.popover--mate-gray {
    color: var(--theme-color-text-always-white);

    background-color: var(--theme-color-surface-inversed-secondary);
    backdrop-filter: blur($blur);
}

.popover-enter-from,
.popover-leave-to {
    opacity: 0;
}
</style>
